<script setup lang="ts">
import type { Calculation } from '@/stores/type/calculation.type'
import { useCalculationStore } from '@/stores/calculation'
import { computed, type PropType, toRef } from 'vue'
import { FavoriteButton } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'
import router from '@/router'

const props = defineProps({
  item: {
    type: Object as PropType<Calculation>,
    required: true
  },
  isCheckboxShown: {
    type: Boolean,
    default: false
  },
  showEditColumn: {
    type: Boolean,
    default: false
  }
})

const item = toRef(props, 'item')

const calculationStore = useCalculationStore()
const { selectedItems } = storeToRefs(calculationStore)

const selectedDatabaseLabel = computed(() => {
  const databaseName = item.value.database_name
  return databaseName || '-'
})

const isSelected = computed<boolean>({
  get: () => selectedItems.value?.includes(item.value) || false,
  set: () => {
    const index = selectedItems.value.findIndex(
      (selectedItem: Calculation) => selectedItem.id === item.value?.id
    )

    index !== -1 ? selectedItems.value.splice(index, 1) : selectedItems.value.push(item.value)
  }
})

const navigateToCalculation = () => {
  const routeName =
    item.value?.status !== STATUS_CHOICES.DRAFT ? 'calculation-result' : 'calculation-form'
  router.push({
    name: routeName,
    params: { id: item.value?.project, calculationId: item.value?.id }
  })
}

const updateFavorite = async () => {
  if (item.value) {
    item.value.favorite = !item.value.favorite
    await calculationStore.update(item.value)
  }
}

const handleChangeCalculation = () => {
  calculationStore.setCurrentItem(item.value)
  calculationStore.openCreateUpdateDialog()
}
</script>

<template>
  <tr v-ripple style="cursor: pointer" @click.stop="navigateToCalculation">
    <td v-if="isCheckboxShown">
      <v-checkbox v-model="isSelected" density="compact" hide-details @click.stop="" />
    </td>
    <td>{{ item.name }}</td>
    <td>{{ item.type }}</td>
    <td>{{ item.phase }}</td>
    <td>{{ selectedDatabaseLabel }}</td>
    <td>{{ item.lifetime }}</td>
    <td>{{ item.status }}</td>
    <td>
      {{
        item.modified ? DateTime.fromISO(item.modified).toLocaleString(DateTime.DATETIME_MED) : '-'
      }}
    </td>
    <td>{{ DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_MED) }}</td>
    <td class="text-center">
      <FavoriteButton :item="item" @update-favorite="updateFavorite" />
    </td>
    <td v-if="showEditColumn" class="text-center">
      <v-btn variant="text" :icon="'$edit'" @click.stop="handleChangeCalculation" />
    </td>
  </tr>
</template>

<style scoped></style>
